import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatCurrency,
  formatCurrencyNoTooltip,
  formatPrice,
  formatWithCommas,
} from "../../assets/helpers";
import {
  CHART_CURSOR,
  CHART_GRID,
  CHART_SURFACE,
  CHART_TICK,
} from "../../assets/helpers/chartPalette";
import CustomChartTooltip from "../CustomChartTooltip/CustomChartTooltip"; // Existing tooltip
import BarChartSkeleton from "../Skeleton/BarchartSkeleton";
import "./StackedBarChart.scss";
import { useSelector } from "react-redux";
import { Empty } from "antd";

const WeeklyTooltip = ({ active, payload, label, drr, format }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="custom-weekly-tooltip"
      style={{ background: "#fff", border: "1px solid #ccc", padding: 10 }}
    >
      <p>
        <strong>{label}</strong>
      </p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color, margin: 0 }}>
          {entry.name} -{" "}
          {drr ? formatCurrency(entry.value, format) : entry.value}
        </p>
      ))}
    </div>
  );
};
const DualBarTooltip = ({ active, payload, label, format, visibleKeys }) => {
  if (!active || !payload?.length) return null;

  const [totalEntry] = payload;
  const monthData = totalEntry?.payload;

  return (
    <div className="tooltip-compact" style={{ height: "max-content" }}>
      <div className="tooltip-static-info">
        <div className="tooltip-row">
          <span
            className="tooltip-key"
            style={{ fontWeight: "500", fontSize: "12px" }}
          >
            {visibleKeys?.includes("total") && visibleKeys?.includes("target")
              ? `Total vs recovered for ${label}`
              : visibleKeys?.includes("total")
              ? `Total for ${label}`
              : `Target for ${label}`}
          </span>
        </div>

        {visibleKeys.includes("target") && (
          <div className="tooltip-row">
            <span className="tooltip-key">Month Target -</span>
            <span className="tooltip-value" style={{ fontSize: "10px" }}>
              {formatCurrency(monthData?.target, format)}
            </span>
          </div>
        )}
        {visibleKeys.includes("total") && (
          <div className="tooltip-row">
            <span className="tooltip-key">Month Total -</span>
            <span className="tooltip-value" style={{ fontSize: "10px" }}>
              {formatCurrency(monthData?.total, format)}
            </span>
          </div>
        )}
      </div>

      <div className="tooltip-dynamic-info">
        {monthData?.weeks?.map((week, index) => (
          <div key={index} className="tooltip-row">
            {visibleKeys.includes("target") && (
              <span className="tooltip-key">Week {week.week} Target -</span>
            )}
            {visibleKeys.includes("target") && (
              <span className="tooltip-value" style={{ fontSize: "10px" }}>
                {formatCurrency(week.week_target, format)}
              </span>
            )}

            {visibleKeys.includes("total") && (
              <>
                <span className="tooltip-key" style={{ marginLeft: 8 }}>
                  Week {week.week} Total -
                </span>
                <span className="tooltip-value" style={{ fontSize: "10px" }}>
                  {formatCurrency(week.week_recovered, format)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StackedBarChart = ({
  data=[],
  totalLoan = 0,
  loading = false,
  variant,
  keys,
  colors,
  yLabel,
  tooltipType = "",
  tooltipMode = "default",
  keysVisible = false,
  chartName = "",
  labelFormatter,
  valueFormatter,
  showYAxis = false,
  height = 320,
  /* thin marks: a 40px bar was wider than the gap between bars, which reads
     as a block of colour rather than a measured quantity */
  barSize = 26,
  drr = false,
  margin = { top: 20, right: 30, left: 10, bottom: 30 },
  /* the grid is a reference, not content: a dashed hairline in the border
     token rather than a solid mid-grey ruled line */
  gridOptions = {
    stroke: CHART_GRID,
    strokeDasharray: "4 4",
    vertical: false,
  },
  xAxisLabelProp = {
    value: chartName,
    position: "insideBottom",
    offset: -1,
    fill: CHART_TICK,
    fontSize: 11,
    fontWeight: 600,
  },
  yAxisLabelProp = {
    value: yLabel,
    angle: -90,
    position: "insideLeft",
    offset: 0,
    fill: CHART_TICK,
    fontSize: 11,
    fontWeight: 600,
  },
  format_type = "normal",
  rotate = false,
  slice = false,
  Intensity = false,
  dualBar = false,
}) => {
  const [visibleKeys, setVisibleKeys] = useState(keys);
  const { format } = useSelector((state) => state.priceFormat);

  useEffect(() => {
    setVisibleKeys(keys);
  }, [keys]);

  const toggleKey = (key) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  /* The rounded end belongs to whichever segment is on top, which is the last
     VISIBLE key - not the last key. Hiding the top series from the legend used
     to leave the stack with a flat, unfinished end. */
  const topKey = keys?.filter((key) => visibleKeys.includes(key)).slice(-1)[0];


  if (loading) {
    return <BarChartSkeleton />;
  }
  
  if(data?.length <=0) {
    return <div
    style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',width:'100%',minHeight:'300px'}}
    >
      <Empty description="No Data"/>
    </div>
  }

  return (
    <>
      <ResponsiveContainer
        width="100%"
        height={height}
        className="stacked-bar-chart"
      >
        <BarChart data={data} margin={margin}>
          <CartesianGrid {...gridOptions} />
          <XAxis
            dataKey="name"
            className="custom-x-axis"
            axisLine={false}
            tickLine={false}
            tick={{ fill: CHART_TICK, fontSize: 11 }}
            label={xAxisLabelProp}
          />
          <YAxis
            className="custom-y-axis"
            axisLine={showYAxis}
            tickLine={false}
            tick={{ fill: CHART_TICK, fontSize: 11 }}
            label={yAxisLabelProp}
            tickFormatter={(value) => {
              return format_type === "currency"
                ? formatCurrencyNoTooltip(value || 0, format)
                : formatWithCommas(value);
            }}
          />

          {/* ✅ Conditional Tooltip */}
          <Tooltip
            content={
              dualBar ? (
                <DualBarTooltip format={format} visibleKeys={visibleKeys} />
              ) : tooltipMode === "weekly" ? (
                <WeeklyTooltip drr={drr} format={format} />
              ) : (
                <CustomChartTooltip
                  variant={variant}
                  labelFormatter={labelFormatter}
                  valueFormatter={valueFormatter}
                  totalLoan={totalLoan}
                  data={data}
                  Intensity={Intensity}
                  format={format}
                  tooltipType={tooltipType}
                />
              )
            }
            cursor={{ fill: CHART_CURSOR }}
          />

          {keys?.map(
            (key, idx) =>
              visibleKeys.includes(key) && (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={dualBar ? undefined : "a"} // <-- stack only if not dualBar
                  fill={colors[idx] || "#8884d8"}
                  barSize={dualBar ? barSize / keys.length : barSize}
                  radius={
                    dualBar
                      ? [4, 4, 0, 0]
                      : key === topKey
                        ? [4, 4, 0, 0]
                        : [0, 0, 0, 0]
                  }
                  /* a 2px gap in the surface colour between segments, so the
                     parts of a stack stay countable */
                  stroke={dualBar ? "none" : CHART_SURFACE}
                  strokeWidth={dualBar ? 0 : 2}
                  isAnimationActive={true}
                />
              )
          )}
        </BarChart>
      </ResponsiveContainer>

      {keysVisible && (
        <div className="chart-items bottom">
          {keys.map((key, idx) => (
            <div
              key={key}
              className={`chart-item ${
                visibleKeys.includes(key) ? "" : "disabled"
              }`}
              onClick={() => toggleKey(key)}
              style={{
                cursor: "pointer",
                opacity: visibleKeys.includes(key) ? 1 : 0.4,
              }}
            >
              <div
                className="color-box"
                style={{ backgroundColor: colors[idx] }}
              ></div>
              <span>{key}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default StackedBarChart;
